import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { generateS3SignedUrl } from '../../src/services/aws/aws-services';
 
/**
* @description Component to render a file action for Pre-Op plan files.
* If only one file is provided, clicking the icon opens the file.
* If multiple files are present, a dropdown is rendered to select and open the files.
* Files are fetched from S3 using signed URLs.
* @memberof PreOpPlanFileAction
*/
class PreOpPlanFileAction extends React.Component {
  state = {
    loading: false,
  };
 
  /**
   * @description Handles opening a file by generating a signed URL from S3 and opening it in a new tab.
   * @param {string} filePath - The path of the file in the S3 bucket.
   */
  handleFileOpen = async (filePath) => {
    const { s3BucketName } = this.props;
    this.setState({ loading: true });
 
    try {
      // Normalize path by removing leading slash if present
      const normalizedPath = filePath.startsWith("/")
        ? filePath.slice(1)
        : filePath;
 
      // Request signed URL from S3 and open it in a new tab
      generateS3SignedUrl(s3BucketName, normalizedPath, (err, signedUrl) => {
        if (signedUrl) {
          window.open(signedUrl, "_blank");
        }
        this.setState({ loadingRowId: null });
      });
    } catch (err) {
      console.error("Failed to open file:", err);
    } finally {
      this.setState({ loading: false });
    }
  };
 
  /**
   * @description Renders the SVG icon used for the file action button.
   * @returns {JSX.Element}
   */
  renderSvgIcon = () => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
      <svg
xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        className="bi bi-box-arrow-up-right"
        viewBox="0 0 16 16"
      >
        <path
          fillRule="evenodd"
          d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5"
        />
        <path
          fillRule="evenodd"
          d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0z"
        />
      </svg>
      {this.state.loading && (
        <div
          className="spinner-border spinner-border-sm text-primary"
          role="status"
          style={{ width: "1rem", height: "1rem" }}
        />
      )}
    </span>
  );
 
/**
   * @description Renders file labels based on path
   * @param {string} file
   * @returns {string}
   */
getFileLabel = (file) => {
    const fileName = file.split("/").pop() || "";
    if (/reverse/i.test(fileName)) return "Reverse";
    if (/anatomic/i.test(fileName)) return "Anatomic";
    return fileName.replace(".pdf", "");
  };
 
  /**
   * @description Renders the component UI based on file count and loading state.
   * - Renders "N/A" if no bucket or files.
   * - Shows spinner if loading.
   * - Shows icon or dropdown for file access.
   * @returns {JSX.Element|string}
   */
  render() {
    const { files = [], s3BucketName } = this.props;
    const { loading } = this.state;
 
    // If no bucket name or no files, return fallback text
    if (!s3BucketName || files.length === 0) return "";
 
    // If single file, render icon to open file directly
    if (files.length === 1) {
        return (
          <span
            role="button"
            onClick={() => !loading && this.handleFileOpen(files[0])}
            style={{ cursor: loading ? "not-allowed" : "pointer" }}
          >
            {this.renderSvgIcon()}
          </span>
        );
      }
 
    // If multiple files, show dropdown with options
    return (
        <Dropdown>
        <Dropdown.Toggle
          data-testid="multi-file-dropdown"
          variant="link"
          className="p-0 border-0"
          as="span"
          style={{ cursor: "pointer" }}
        >
          {this.renderSvgIcon()}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {files.map((file, idx) => (
            <Dropdown.Item
              key={idx}
              onClick={() => !loading && this.handleFileOpen(file)}
              disabled={loading}
            >
              {this.getFileLabel(file)}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
}
 
export default PreOpPlanFileAction;